import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, Paper, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import apiService from '../services/api.service';
import GoldenArrow from '../components/common/GoldenArrow';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

type ViewKey = 'all' | 'drafts' | 'pending-approvals' | 'approved' | 'in-progress';

const VIEW_OPTIONS: Array<{ key: ViewKey; label: string }> = [
  { key: 'all', label: 'All My POs' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'pending-approvals', label: 'Pending Approvals' },
  { key: 'approved', label: 'Approved / Posted' },
  { key: 'in-progress', label: 'In Progress' },
];

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const POTracker: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = String(searchParams.get('view') || 'all').toLowerCase() as ViewKey;
  const currentView: ViewKey = VIEW_OPTIONS.some((v) => v.key === requestedView) ? requestedView : 'all';

  const [loading, setLoading] = useState(false);
  const [myPOs, setMyPOs] = useState<any[]>([]);
  const [myDrafts, setMyDrafts] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [approverPOs, setApproverPOs] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    // Sync DocuSign PO statuses in background (fire-and-forget)
    apiService.syncDocuSignPOs().catch(() => {});
    try {
      const [myRes, draftRes, pendingRes, approverRes] = await Promise.all([
        apiService.getMyPOs(),
        apiService.getMyPODrafts(),
        apiService.getPendingApprovalsPO(),
        apiService.getApproverPOs(),
      ]);

      setMyPOs(Array.isArray((myRes as any)?.data) ? (myRes as any).data : []);
      setMyDrafts(Array.isArray((draftRes as any)?.data) ? (draftRes as any).data : []);
      setPendingApprovals(Array.isArray((pendingRes as any)?.data) ? (pendingRes as any).data : []);
      setApproverPOs(Array.isArray((approverRes as any)?.data) ? (approverRes as any).data : []);
    } catch {
      setMyPOs([]);
      setMyDrafts([]);
      setPendingApprovals([]);
      setApproverPOs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onUpdated = () => { void load(); };
    window.addEventListener('po-updated', onUpdated as any);
    window.addEventListener('company-changed', onUpdated as any);
    return () => {
      window.removeEventListener('po-updated', onUpdated as any);
      window.removeEventListener('company-changed', onUpdated as any);
    };
  }, [load]);

  const mergedInProgress = useMemo(() => {
    const seen = new Set<number>();
    return [...myPOs, ...approverPOs]
      .filter((po: any) => {
        const id = Number(po.poid || po.POID || 0);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .filter((po: any) => {
        const status = String(po.status || '').toLowerCase();
        const inbox = String(po.inboxstatus || '').toLowerCase();
        const posted = status.includes('posted') || Number(po.sappodocnum || po.SAPPODocNum || 0) > 0;
        const approved = status === 'approved';
        const rejected = status === 'rejected';
        const draft = status === 'draft';
        const pending = status.startsWith('pending');
        return !draft && !posted && !approved && !rejected && (pending || inbox === 'in progress');
      });
  }, [myPOs, approverPOs]);

  const approvedRows = useMemo(
    () => myPOs.filter((po: any) => {
      const status = String(po.status || '').toLowerCase();
      const inbox = String(po.inboxstatus || '').toLowerCase();
      return status === 'approved' || status === 'postedtosap' || inbox === 'completed';
    }),
    [myPOs]
  );

  const counts = useMemo(() => ({
    all: myPOs.length,
    drafts: myDrafts.length,
    'pending-approvals': pendingApprovals.length,
    approved: approvedRows.length,
    'in-progress': mergedInProgress.length,
  }), [approvedRows.length, mergedInProgress.length, myDrafts.length, myPOs.length, pendingApprovals.length]);

  const rows = useMemo(() => {
    switch (currentView) {
      case 'drafts':
        return myDrafts;
      case 'pending-approvals':
        return pendingApprovals;
      case 'approved':
        return approvedRows;
      case 'in-progress':
        return mergedInProgress;
      case 'all':
      default:
        return myPOs;
    }
  }, [approvedRows, currentView, mergedInProgress, myDrafts, myPOs, pendingApprovals]);

  const title = useMemo(() => {
    const found = VIEW_OPTIONS.find((v) => v.key === currentView);
    return found ? found.label : 'PO Tracker';
  }, [currentView]);

  const [columnDefs] = useState([
    {
      field: 'ponumber',
      headerName: 'PO Number',
      width: 250,
      cellRenderer: (params: ICellRendererParams) => {
        const poId = Number(params.data.poid || params.data.POID || 0);
        const sapPO = params.data.sappodocnum || params.data.SAPPODocNum;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap' }} noWrap>
                {params.data.ponumber || ''}
              </Typography>
              {sapPO ? (
                <Typography component="span" sx={{ color: '#F57C00', fontSize: '11px', ml: 1 }}>
                  (SAP: {sapPO})
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <GoldenArrow docType="PO" poId={poId} docId={poId} />
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'podate',
      headerName: 'PO Date',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {toDDMMYYYY(params.data.podate)}
        </Typography>
      )
    },
    {
      field: 'requestedbyname',
      headerName: 'Requester',
      width: 170,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }} noWrap title={params.data.requestedbyname}>
          {params.data.requestedbyname}
        </Typography>
      )
    },
    {
      field: 'vendorname',
      headerName: 'Vendor',
      width: 180,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }} noWrap title={params.data.vendorname}>
          {params.data.vendorname || params.data.vendorcode || ''}
        </Typography>
      )
    },
    {
      field: 'requisitiontype',
      headerName: 'Type',
      width: 110,
    },
    {
      field: 'allocationtype',
      headerName: 'Allocation Type',
      width: 130,
    },
    {
      field: 'allocationcode',
      headerName: 'Allocation Code',
      width: 140,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      cellRenderer: (params: ICellRendererParams) => {
        const status = String(params.data.status || '').toLowerCase();
        const posted = status.includes('posted') || Number(params.data.sappodocnum || params.data.SAPPODocNum || 0) > 0;
        const approved = status === 'approved';
        const pending = status.startsWith('pending');
        const draft = status === 'draft';
        const bgColor = draft ? '#ECEFF1' : posted ? '#FFF3E0' : approved ? '#E8F5E9' : pending ? '#FFF8E1' : '#E3F2FD';
        const textColor = draft ? '#455A64' : posted ? '#F57C00' : approved ? '#2E7D32' : pending ? '#E65100' : '#1565C0';
        const displayStatus = posted && !status.includes('posted') ? 'Posted to SAP' : (params.data.status || '');
        return (
          <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: bgColor, width: '100%' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: textColor }} noWrap>
              {displayStatus}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'currentstep',
      headerName: 'Step',
      width: 90,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400, textAlign: 'center', display: 'block' }}>
          {params.data.currentstep || ''}
        </Typography>
      )
    },
    {
      field: 'requireddate',
      headerName: 'Needed By',
      width: 120,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
          {toDDMMYYYY(params.data.requireddate)}
        </Typography>
      )
    },
    {
      field: 'totalamount',
      headerName: 'Amount',
      width: 130,
      cellRenderer: (params: ICellRendererParams) => (
        <Typography sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'right', display: 'block' }}>
          {Number(params.data.totalamount || 0).toFixed(2)} {params.data.currency || ''}
        </Typography>
      )
    }
  ]);

  return (
    <>
      <Paper
        component="section"
        aria-labelledby="po-tracker-heading"
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          height: 'calc(100vh - 100px)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'IBM Plex Sans', sans-serif",
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 1.5, sm: 0 }}
          mb={2}
        >
          <Box>
            <Typography
              component="h1"
              id="po-tracker-heading"
              variant="h5"
              sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: '#1a1a1a' }}
            >
              PO Tracker
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {title}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              onClick={() => setSearchParams({ view: 'all' })}
              sx={{ flex: { xs: 1, sm: 'unset' } }}
            >
              Reset View
            </Button>
            <Button
              variant="outlined"
              onClick={load}
              disabled={loading}
              sx={{ flex: { xs: 1, sm: 'unset' } }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ mb: 2, overflowX: 'auto' }}>
          <ButtonGroup
            variant="outlined"
            size="small"
            aria-label="View filter"
            sx={{ flexWrap: 'nowrap' }}
          >
            {VIEW_OPTIONS.map((view) => (
              <Button
                key={view.key}
                variant={currentView === view.key ? 'contained' : 'outlined'}
                onClick={() => setSearchParams({ view: view.key })}
                aria-pressed={currentView === view.key}
              >
                {view.label} ({counts[view.key]})
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 0, overflowX: 'auto' }}>
          <div
            className="ag-theme-alpine"
            role="grid"
            aria-label="PO Tracker"
            style={{
              height: '100%',
              width: '100%',
              minWidth: 720,
              border: '2px solid #F0AB00',
              fontFamily: "'Inter', 'IBM Plex Sans', sans-serif"
            }}
          >
            <AgGridReact
              rowData={rows}
              columnDefs={columnDefs}
              domLayout="normal"
              rowHeight={36}
              headerHeight={40}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                flex: 0,
                minWidth: 90,
              }}
              pagination={true}
              paginationPageSize={25}
              paginationPageSizeSelector={[25, 50, 100]}
              suppressPaginationPanel={false}
              getRowId={(params: any) => String(params.data.poid || params.data.POID || params.data.id)}
              getRowStyle={(params: any) => ({
                backgroundColor: (params.node.rowIndex ?? 0) % 2 === 0 ? '#FFFFFF' : '#F8F8F8'
              })}
              theme="legacy"
              overlayLoadingTemplate="<span></span>"
              overlayNoRowsTemplate='<div style="padding: 20px; color: #666; font-family: Inter, sans-serif;">No PO records to display</div>'
            />
          </div>
        </Box>
      </Paper>
      <style>{`
        .ag-header-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #1a1a1a !important;
          background: #f5f5f5 !important;
          border-bottom: 2px solid #F0AB00 !important;
        }

        .ag-header-cell-label {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
        }

        .ag-cell {
          font-family: 'Inter', 'IBM Plex Sans', sans-serif !important;
          font-size: 12px !important;
          display: flex !important;
          align-items: center !important;
        }
      `}</style>
    </>
  );
};

export default POTracker;
